import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { UserPlus, Building2, CheckCircle2, FileText, Sparkles, AlertCircle, Upload, ArrowRight, BookmarkCheck, Eye, EyeOff } from 'lucide-react';

export default function PublicRegister() {
    const [mode, setMode] = useState('candidate'); // 'candidate' or 'company'
    const [sectors, setSectors] = useState([]);
    const [showCandidatePassword, setShowCandidatePassword] = useState(false);
    
    // Candidate Form State
    const [candidateData, setCandidateData] = useState({
        name: '',
        gender: 'Male',
        highest_education: 'Graduate / B.Tech / B.Sc / B.Com',
        city: '',
        tehsil: '',
        email: '',
        phone_number: '',
        password: '',
        experience_type: 'Fresher',
        years_of_experience: '0 Years',
        last_company_name: '',
        last_designation: '',
    });
    const [selectedSectors, setSelectedSectors] = useState([]);
    const [resumeFile, setResumeFile] = useState(null);

    // Company Form State
    const [companyData, setCompanyData] = useState({
        company_name: '',
        office_location: '',
        openings: '',
        contact_person_name: '',
        contact_person_number: '',
        contact_person_email: '',
        company_phone: '',
        company_email: '',
    });
    const [companySectors, setCompanySectors] = useState([]);
    const [customCompanySector, setCustomCompanySector] = useState('');
    const [customCandidateSector, setCustomCandidateSector] = useState('');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [successResult, setSuccessResult] = useState(null);

    const handleAddCustomCompanySector = () => {
        const trimmed = customCompanySector.trim();
        if (trimmed && !companySectors.includes(trimmed)) {
            setCompanySectors([...companySectors, trimmed]);
            if (!sectors.includes(trimmed)) {
                setSectors([...sectors, trimmed]);
            }
            setCustomCompanySector('');
        }
    };

    const handleAddCustomCandidateSector = () => {
        const trimmed = customCandidateSector.trim();
        if (trimmed && !selectedSectors.includes(trimmed)) {
            setSelectedSectors([...selectedSectors, trimmed]);
            if (!sectors.includes(trimmed)) {
                setSectors([...sectors, trimmed]);
            }
            setCustomCandidateSector('');
        }
    };

    useEffect(() => {
        axios.get('/api/sectors').then(res => {
            if (res.data.success) setSectors(res.data.sectors);
        }).catch(() => {});
    }, []);

    const toggleSector = (sec, list, setList) => {
        if (list.includes(sec)) {
            setList(list.filter(s => s !== sec));
        } else {
            setList([...list, sec]);
        }
    };

    const handleCandidateChange = (field, value) => {
        setCandidateData(prev => ({ ...prev, [field]: value }));
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleCompanyChange = (field, value) => {
        setCompanyData(prev => ({ ...prev, [field]: value }));
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const handleCandidateSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});
        setLoading(true);

        const formData = new FormData();
        Object.keys(candidateData).forEach(key => {
            formData.append(key, candidateData[key]);
        });
        formData.append('job_applying_for', selectedSectors.join(', '));
        if (resumeFile) {
            formData.append('resume', resumeFile);
        }

        try {
            const res = await axios.post('/api/applicants/public-register', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.success) {
                setSuccessResult({ type: 'applicant', data: res.data.applicant });
            }
        } catch (err) {
            if (err.response?.status === 422 && err.response?.data?.errors) {
                setFieldErrors(err.response.data.errors);
                setError(err.response.data.message || 'Registration failed. Please check the fields below.');
            } else {
                setError(err.response?.data?.message || 'Registration failed. Please check inputs.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCompanySubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});
        setLoading(true);

        try {
            const payload = {
                ...companyData,
                sector: companySectors.join(', '),
            };
            const res = await axios.post('/api/companies/public-register', payload);
            if (res.data.success) {
                setSuccessResult({ type: 'company', data: res.data.company, hr_user: res.data.hr_user });
            }
        } catch (err) {
            if (err.response?.status === 422 && err.response?.data?.errors) {
                setFieldErrors(err.response.data.errors);
                setError(err.response.data.message || 'Company registration failed. Please check the fields below.');
            } else {
                setError(err.response?.data?.message || 'Company registration failed.');
            }
        } finally {
            setLoading(false);
        }
    };

    const renderFieldError = (field) => {
        if (!fieldErrors[field]) return null;
        const msg = Array.isArray(fieldErrors[field]) ? fieldErrors[field][0] : fieldErrors[field];
        return (
            <p className="text-red-400 text-xs font-semibold mt-1 flex items-center space-x-1">
                <span>⚠️ {msg}</span>
            </p>
        );
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 relative">
            <div className="text-center mb-8">
                <span className="text-xs font-bold uppercase tracking-wider text-indigo-400 bg-indigo-500/10 px-3 py-1 rounded-full border border-indigo-500/20">
                    Registration Portal
                </span>
                <h1 className="text-3xl font-extrabold mt-3 bg-gradient-to-r from-white via-slate-100 to-indigo-300 bg-clip-text text-transparent">
                    Job Fair Registration Form
                </h1>
                <p className="text-sm text-slate-400 mt-1">Register for Mega Job Fair 2026 • Free Entry & Stall Queue Slip</p>
            </div>

            {/* Success Slip View */}
            {successResult ? (
                <div className="glass-card rounded-2xl p-8 border border-emerald-500/30 text-center shadow-2xl animate-fade-in">
                    <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-500/40 glow-emerald">
                        <CheckCircle2 className="w-10 h-10" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">Registration Successful!</h2>
                    <p className="text-sm text-emerald-300 mt-1">
                        {successResult.type === 'applicant' 
                            ? 'Your Job Fair Slip is generated with your stall visit sequence.' 
                            : 'Company registered successfully for the Job Fair!'}
                    </p>

                    {successResult.type === 'applicant' && (
                        <div className="mt-6 bg-slate-900/90 rounded-xl p-6 text-left border border-slate-800 space-y-4">
                            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                                <div>
                                    <div className="text-lg font-bold text-white">{successResult.data.name}</div>
                                    <div className="text-xs text-slate-400">📱 {successResult.data.phone_number} | ✉️ {successResult.data.email}</div>
                                </div>
                                <span className="text-xs font-bold bg-indigo-500/20 text-indigo-300 px-3 py-1 rounded-lg border border-indigo-500/30">
                                    Registration ID: #{successResult.data.id}
                                </span>
                            </div>

                            <div>
                                <div className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-2 flex items-center space-x-1">
                                    <Sparkles className="w-4 h-4" />
                                    <span>Assigned Stall Visit Queue (Order 1, 2, 3)</span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {successResult.data.company_visits?.map((visit, idx) => (
                                        <div key={visit.id} className="p-3 bg-slate-950/80 rounded-xl border border-slate-800">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-[10px] font-extrabold text-indigo-400 bg-indigo-500/20 px-2 py-0.5 rounded">
                                                    STALL #{visit.visit_order || (idx + 1)}
                                                </span>
                                                <span className="text-[10px] text-amber-400 font-bold uppercase">{visit.status}</span>
                                            </div>
                                            <div className="font-bold text-sm text-slate-200 truncate">{visit.company?.company_name}</div>
                                            <div className="text-[11px] text-slate-400">{visit.company?.office_location || 'Indore Stall'}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="p-3 rounded-lg bg-indigo-950/40 border border-indigo-500/20 text-xs text-indigo-200">
                                💡 <strong>Login Note:</strong> You can log in using your Phone Number <code>{successResult.data.phone_number}</code> and Default Password <code>123456</code> to check real-time interview results!
                            </div>
                        </div>
                    )}

                    {successResult.type === 'company' && successResult.hr_user && (
                        <div className="mt-6 bg-slate-900/90 rounded-xl p-6 text-left border border-slate-800 space-y-3">
                            <div className="text-sm font-bold text-purple-300">🏢 Company HR Stall Login Credentials Created</div>
                            <div className="text-xs text-slate-300">
                                👤 <strong>HR Name:</strong> {successResult.hr_user.name}<br />
                                ✉️ <strong>Login Email/Mobile:</strong> <code>{successResult.hr_user.email}</code> / <code>{successResult.hr_user.mobile_number}</code><br />
                                🔑 <strong>Default Password:</strong> <code>123456</code> (Role: <code>Company HR</code>)
                            </div>
                            <div className="p-3 rounded-lg bg-purple-950/40 border border-purple-500/20 text-xs text-purple-200">
                                💡 Company HR can log in using Staff Login to access the <strong>Stall Interview Portal</strong> and mark candidate interview statuses.
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex justify-center space-x-4">
                        <button
                            onClick={() => { setSuccessResult(null); setFieldErrors({}); setError(''); }}
                            className="px-6 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm transition-all"
                        >
                            Register Another Person
                        </button>
                    </div>
                </div>
            ) : (
                <div className="glass-card rounded-2xl p-6 sm:p-8 border border-slate-800 shadow-2xl">
                    {/* Toggle Buttons */}
                    <div className="flex bg-slate-900/90 p-1.5 rounded-xl mb-6 border border-slate-800">
                        <button
                            type="button"
                            onClick={() => { setMode('candidate'); setError(''); setFieldErrors({}); }}
                            className={`flex-1 py-3 rounded-lg text-sm font-bold transition-all flex items-center justify-center space-x-2 ${
                                mode === 'candidate' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/30' : 'text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            <UserPlus className="w-4 h-4" />
                            <span>Candidate Registration</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => { setMode('company'); setError(''); setFieldErrors({}); }}
                            className={`flex-1 py-3 rounded-lg text-sm font-bold transition-all flex items-center justify-center space-x-2 ${
                                mode === 'company' ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30' : 'text-slate-400 hover:text-slate-200'
                            }`}
                        >
                            <Building2 className="w-4 h-4" />
                            <span>Company Registration</span>
                        </button>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm flex items-center space-x-3">
                            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Candidate Registration Form */}
                    {mode === 'candidate' && (
                        <form onSubmit={handleCandidateSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Full Name *</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. Ramesh Verma"
                                        value={candidateData.name}
                                        onChange={e => handleCandidateChange('name', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.name ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('name')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Gender</label>
                                    <select
                                        value={candidateData.gender}
                                        onChange={e => handleCandidateChange('gender', e.target.value)}
                                        className="w-full px-4 py-2.5 rounded-xl glass-input text-sm bg-slate-900 text-white"
                                    >
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    {renderFieldError('gender')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Mobile Number (Login ID) *</label>
                                    <input
                                        type="tel"
                                        required
                                        placeholder="e.g. 9876543210"
                                        value={candidateData.phone_number}
                                        onChange={e => handleCandidateChange('phone_number', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.phone_number ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('phone_number')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Email Address *</label>
                                    <input
                                        type="email"
                                        required
                                        placeholder="ramesh@gmail.com"
                                        value={candidateData.email}
                                        onChange={e => handleCandidateChange('email', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.email ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('email')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Password (Optional, default 123456)</label>
                                    <div className="relative">
                                        <input
                                            type={showCandidatePassword ? 'text' : 'password'}
                                            placeholder="Leave blank for default (123456)"
                                            value={candidateData.password}
                                            onChange={e => handleCandidateChange('password', e.target.value)}
                                            className={`w-full pl-4 pr-10 py-2.5 rounded-xl glass-input text-sm ${
                                                fieldErrors.password ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                            }`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCandidatePassword(!showCandidatePassword)}
                                            className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-200 transition-colors"
                                            title={showCandidatePassword ? 'Hide password' : 'Show password'}
                                        >
                                            {showCandidatePassword ? <EyeOff className="w-4 h-4 text-indigo-400" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    {renderFieldError('password')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Highest Education</label>
                                    <input
                                        type="text"
                                        placeholder="B.Tech, B.Com, MBA, 12th, ITI"
                                        value={candidateData.highest_education}
                                        onChange={e => handleCandidateChange('highest_education', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.highest_education ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('highest_education')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">City</label>
                                    <input
                                        type="text"
                                        placeholder="Indore, Bhopal, Ujjain"
                                        value={candidateData.city}
                                        onChange={e => handleCandidateChange('city', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.city ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('city')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Tehsil / Area</label>
                                    <input
                                        type="text"
                                        placeholder="Sanwer, Vijay Nagar"
                                        value={candidateData.tehsil}
                                        onChange={e => handleCandidateChange('tehsil', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.tehsil ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('tehsil')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Experience Type</label>
                                    <select
                                        value={candidateData.experience_type}
                                        onChange={e => handleCandidateChange('experience_type', e.target.value)}
                                        className="w-full px-4 py-2.5 rounded-xl glass-input text-sm bg-slate-900 text-white"
                                    >
                                        <option value="Fresher">Fresher</option>
                                        <option value="Experienced">Experienced</option>
                                    </select>
                                    {renderFieldError('experience_type')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Years of Experience</label>
                                    <input
                                        type="text"
                                        placeholder="0 Years, 2 Years"
                                        value={candidateData.years_of_experience}
                                        onChange={e => handleCandidateChange('years_of_experience', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.years_of_experience ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('years_of_experience')}
                                </div>
                            </div>

                            {/* Preferred Sectors Tag Selection */}
                            <div>
                                <label className="block text-xs font-bold text-indigo-300 mb-2 flex items-center space-x-1">
                                    <BookmarkCheck className="w-4 h-4 text-indigo-400" />
                                    <span>Select Preferred Job Sectors (Auto-matches companies):</span>
                                </label>

                                <div className="flex flex-wrap gap-2 p-3 bg-slate-900/80 rounded-xl border border-slate-800">
                                    {sectors.map(sec => {
                                        const isSelected = selectedSectors.includes(sec);
                                        return (
                                            <button
                                                type="button"
                                                key={sec}
                                                onClick={() => toggleSector(sec, selectedSectors, setSelectedSectors)}
                                                className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                                                    isSelected
                                                        ? 'bg-indigo-600 text-white border-indigo-500 shadow-md shadow-indigo-600/30 font-bold'
                                                        : 'bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700 hover:text-slate-200'
                                                }`}
                                            >
                                                {isSelected ? '✓ ' : '+ '}{sec}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Custom Sector Input for Candidate */}
                                <div className="mt-2.5 flex items-center space-x-2">
                                    <input
                                        type="text"
                                        value={customCandidateSector}
                                        onChange={e => setCustomCandidateSector(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomCandidateSector(); } }}
                                        placeholder="Type custom preferred sector (e.g. Solar Energy, Event Management)..."
                                        className="flex-1 px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:border-indigo-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddCustomCandidateSector}
                                        className="px-3.5 py-2 rounded-xl bg-indigo-700 hover:bg-indigo-600 text-white text-xs font-bold shrink-0 shadow-md"
                                    >
                                        + Add Sector
                                    </button>
                                </div>
                                {renderFieldError('job_applying_for')}
                            </div>

                            {/* Upload Resume */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-300 mb-1">Upload Resume (PDF / DOCX)</label>
                                <div className="relative border-2 border-dashed border-slate-800 rounded-xl p-4 text-center hover:border-indigo-500/40 transition-colors bg-slate-900/50">
                                    <input
                                        type="file"
                                        accept=".pdf,.doc,.docx,.jpg,.png"
                                        onChange={e => setResumeFile(e.target.files[0])}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    />
                                    <Upload className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                                    <div className="text-xs text-slate-300">
                                        {resumeFile ? <span className="font-bold text-indigo-400">{resumeFile.name}</span> : 'Click or drop your resume here'}
                                    </div>
                                </div>
                                {renderFieldError('resume')}
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold text-sm shadow-xl shadow-indigo-600/30 transition-all flex items-center justify-center space-x-2"
                            >
                                {loading ? <span>Generating Job Fair Slip...</span> : (
                                    <>
                                        <span>Submit Candidate Registration</span>
                                        <ArrowRight className="w-4 h-4" />
                                    </>
                                )}
                            </button>
                        </form>
                    )}

                    {/* Company Registration Form */}
                    {mode === 'company' && (
                        <form onSubmit={handleCompanySubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Company Name *</label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g. Tata Consultancy Services"
                                        value={companyData.company_name}
                                        onChange={e => handleCompanyChange('company_name', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.company_name ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('company_name')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Office Location</label>
                                    <input
                                        type="text"
                                        placeholder="Indore IT Park, MP"
                                        value={companyData.office_location}
                                        onChange={e => handleCompanyChange('office_location', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.office_location ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('office_location')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Vacancies / Openings Details</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. 50 Openings for Junior Software Dev"
                                        value={companyData.openings}
                                        onChange={e => handleCompanyChange('openings', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.openings ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('openings')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Contact Person Name</label>
                                    <input
                                        type="text"
                                        placeholder="HR Manager Name"
                                        value={companyData.contact_person_name}
                                        onChange={e => handleCompanyChange('contact_person_name', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.contact_person_name ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('contact_person_name')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Contact Person Mobile</label>
                                    <input
                                        type="tel"
                                        placeholder="9876543210"
                                        value={companyData.contact_person_number}
                                        onChange={e => handleCompanyChange('contact_person_number', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.contact_person_number ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('contact_person_number')}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-300 mb-1">Contact Person Email</label>
                                    <input
                                        type="email"
                                        placeholder="hr@tcs.com"
                                        value={companyData.contact_person_email}
                                        onChange={e => handleCompanyChange('contact_person_email', e.target.value)}
                                        className={`w-full px-4 py-2.5 rounded-xl glass-input text-sm ${
                                            fieldErrors.contact_person_email ? 'border-red-500 focus:border-red-500 ring-1 ring-red-500/30' : ''
                                        }`}
                                    />
                                    {renderFieldError('contact_person_email')}
                                </div>
                            </div>

                            {/* Company Sectors */}
                            <div>
                                <label className="block text-xs font-bold text-purple-300 mb-2 flex items-center space-x-1">
                                    <Building2 className="w-4 h-4 text-purple-400" />
                                    <span>Select Sectors your company operates in (Auto-suggestions for Candidates):</span>
                                </label>

                                <div className="flex flex-wrap gap-2 p-3 bg-slate-900/80 rounded-xl border border-slate-800">
                                    {sectors.map(sec => {
                                        const isSelected = companySectors.includes(sec);
                                        return (
                                            <button
                                                type="button"
                                                key={sec}
                                                onClick={() => toggleSector(sec, companySectors, setCompanySectors)}
                                                className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                                                    isSelected
                                                        ? 'bg-purple-600 text-white border-purple-500 shadow-md shadow-purple-600/30 font-bold'
                                                        : 'bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700 hover:text-slate-200'
                                                }`}
                                            >
                                                {isSelected ? '✓ ' : '+ '}{sec}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Custom Sector Input */}
                                <div className="mt-2.5 flex items-center space-x-2">
                                    <input
                                        type="text"
                                        value={customCompanySector}
                                        onChange={e => setCustomCompanySector(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomCompanySector(); } }}
                                        placeholder="Type custom sector name (e.g. Solar Energy, Event Management)..."
                                        className="flex-1 px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:border-purple-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddCustomCompanySector}
                                        className="px-3.5 py-2 rounded-xl bg-purple-700 hover:bg-purple-600 text-white text-xs font-bold shrink-0 shadow-md"
                                    >
                                        + Add Sector
                                    </button>
                                </div>
                                {renderFieldError('sector')}
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-sm shadow-xl shadow-purple-600/30 transition-all flex items-center justify-center space-x-2"
                            >
                                {loading ? <span>Registering Company...</span> : (
                                    <>
                                        <span>Submit Company Registration</span>
                                        <ArrowRight className="w-4 h-4" />
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            )}
        </div>
    );
}
